import { useCallback, useState } from "react";
import { Table, Space, Tag, Button, Checkbox, Popover, message, Popconfirm } from "antd";
import { useRequest } from "../../hooks";
import { searchTask, closeTask, assignTask, batchComplete } from "../../api/task";
import TaskFilter from './Filter';
import { dateTimeRender, textRender } from "../../utils";
import { TaskStatus, TaskStatusOptions, TaskTypeOptions, TaskType } from "../../constants";
import UserLookup from "../common/UserLookup";
import IfNotObserver from "../common/IfNotObserver";

export default function TaskList() {
    const [searchParams, setSearchParams] = useState<Partial<SearchTaskParams>>({
        sortField: 'createDatetime',
        asc: true,
        taskStatus: TaskStatus.INITIAL,
        taskType: TaskType.DEVICE_ALARM,
    });
    const [pagination, setPagination] = useState({
        pageNum: 1,
        pageSize: 10,
    });
    const fetchData = useCallback(() => {
        return searchTask({
            ...searchParams,
            ...pagination,
        })
    }, [searchParams, pagination]);
    const { data, loading } = useRequest(fetchData);
    const onSearch = () => {
        setSearchParams({
            ...searchParams,
        })
    };

    const [assigning, setAssigning] = useState(false);
    const [assignee, setAssignee] = useState<string>()
    const [assigningTaskId, setSssigningTaskId] = useState<number | string>()
    const [selectedRows, setSelectedRows] = useState<any[]>([]);
    const [batchActionLoading, setBatchActionLoading] = useState(false)
    const batchCompleteSelected = () => {
        setBatchActionLoading(true)
        batchComplete(selectedRows.map(i => i.taskId))
            .then(() => {
                message.success('操作成功')
                setPagination({
                    ...pagination
                })
                setSelectedRows([])
            })
            .catch(err => {
                message.error(err.message || '操作失败,请重试')
            })
            .finally(() => {
                setBatchActionLoading(false)
            })
    }
    return (
        <div>
            <Space style={{ display: 'flex', justifyContent: 'space-between' }}>
                <TaskFilter values={searchParams} onChange={(v) => {
                    setPagination({
                        ...pagination,
                        pageNum: 1,
                    })
                    setSearchParams(v)
                }} onSearch={onSearch}
                    slot={<Button loading={batchActionLoading} onClick={() => {
                        batchCompleteSelected()
                    }} type="primary" disabled={selectedRows.length === 0}>批量完成</Button>}
                />
            </Space>
            <br />
            <Table
                rowKey='taskId'
                rowSelection={{
                    renderCell(checked, record, index, originNode) {
                        return (
                            record.taskStatus === TaskStatus.INITIAL || record.taskStatus === TaskStatus.ACCEPT
                        ) ? originNode : <Checkbox disabled />
                    },
                    selectedRowKeys: selectedRows.map(i => i.taskId),
                    onChange(_, selectedRows) {
                        setSelectedRows(selectedRows)
                    },
                }}
                scroll={{ x: 1 }}
                pagination={{
                    pageSize: pagination.pageSize,
                    current: pagination.pageNum,
                    total: data?.total,
                    onChange: (pageNum, pageSize) => {
                        setPagination({
                            ...pagination,
                            pageSize,
                            pageNum
                        })
                    }
                }}
                columns={[
                    {
                        dataIndex: 'taskId',
                        title: 'ID',
                        fixed: true,
                        width: 100,
                    },
                    {
                        fixed: true,
                        dataIndex: 'projectName',
                        title: '展会名称',
                        width: 140,
                        render: textRender
                    },
                    {
                        dataIndex: 'description',
                        title: '工单描述',
                        fixed: true,
                        width: 400,
                        render: textRender
                    },
                    {
                        dataIndex: 'taskType',
                        title: '工单类型',
                        width: 100,
                        render: (taskType: string) => {
                            return TaskTypeOptions.find(i => i.value === taskType)?.label
                        }
                    },

                    {
                        dataIndex: 'taskStatus',
                        title: '工单状态',
                        width: 100,
                        render: (taskStatus: string) => {
                            const text = TaskStatusOptions.find(i => i.value === taskStatus)?.label;
                            return <Tag color={taskStatus === TaskStatus.ACCEPT ? '#adc6ff' :
                                taskStatus === TaskStatus.INITIAL ?
                                    'red' : 'green'}>{text}</Tag>
                        }
                    },
                    {
                        dataIndex: 'acceptUser',
                        title: '认领人',
                        width: 100,
                        render: (acceptor: RelatedUser) => {
                            return textRender(acceptor?.username)
                        }
                    },
                    {
                        dataIndex: 'acceptTime',
                        title: '认领时间',
                        width: 200,
                        render: dateTimeRender
                    },
                    {
                        dataIndex: 'completeUser',
                        title: '完成确认人',
                        width: 150,
                        render: (completeUser: RelatedUser) => textRender(completeUser?.username)
                    },
                    {
                        dataIndex: 'completeTime',
                        title: '完成确认时间',
                        width: 200,
                        render: dateTimeRender
                    },
                    {
                        dataIndex: 'createDatetime',
                        title: '创建时间',
                        width: 200,
                        render: textRender
                    },
                    {
                        dataIndex: 'actions',
                        width: 120,
                        title: '操作',
                        render: (_, row) => {
                            return <IfNotObserver>
                                <div>
                                    <Popover open={assigningTaskId === row.taskId}
                                        title='请选择分配人员'
                                        content={(
                                            <div>
                                                <div>
                                                    <UserLookup value={assignee} onChange={setAssignee} />
                                                </div>
                                                <Space style={{ marginTop: 50 }}>
                                                    <Button size='small' onClick={() => {
                                                        setSssigningTaskId(undefined)
                                                        setAssignee(undefined)
                                                    }}>取消</Button>
                                                    <Button size='small' disabled={!assignee} loading={assigning} type='primary' onClick={() => {
                                                        assignTask(assignee!, row.taskId)
                                                            .then(() => {
                                                                setAssigning(false)
                                                                message.success('分配成功')
                                                                setSssigningTaskId(undefined)
                                                                setAssignee(undefined)
                                                                onSearch()
                                                            }).catch(err => {
                                                                message.error('分配失败, 请重试')
                                                                setAssigning(false)
                                                            })
                                                    }}>确认</Button>
                                                </Space>
                                            </div>
                                        )}
                                    >
                                        <Button style={{ paddingLeft: 0 }} disabled={row.taskStatus === TaskStatus.CLOSED || row.taskStatus === TaskStatus.COMPLETED} type='link'
                                            onClick={() => {
                                                setAssignee(undefined)
                                                setSssigningTaskId(row.taskId)
                                            }}>分配</Button>
                                    </Popover>

                                    <Popconfirm title='确认完成?' onConfirm={async () => {
                                        await closeTask(row.taskId)
                                        onSearch()
                                    }}>
                                        <Button style={{ padding: 0 }} disabled={row.taskStatus === TaskStatus.CLOSED || row.taskStatus === TaskStatus.COMPLETED} type='link'>完成</Button>
                                    </Popconfirm>

                                </div>
                            </IfNotObserver>
                        }
                    },
                ]}
                loading={loading}
                dataSource={data?.data}
            />
        </div>
    )
}
